import React from 'react';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, PointElement, ArcElement, Title, Tooltip, Legend, Filler } from 'chart.js';
import { mockAnalytics, mockLeads, mockProjects } from '../store/mockData';
import { StatCard, formatCurrency, ProgressBar } from '../components/common';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, ArcElement, Title, Tooltip, Legend, Filler);

const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const a = mockAnalytics;

export const AnalyticsPage = () => {
  const revenueVsCost = {
    labels: months,
    datasets: [
      { label: 'Revenue', data: a.monthlyRevenue, backgroundColor: 'rgba(232, 83, 10, 0.85)', borderRadius: 6 },
      { label: 'Material Cost', data: a.monthlyRevenue.map(v => Math.round(v * 0.4)), backgroundColor: 'rgba(232, 83, 10, 0.2)', borderRadius: 6 },
    ],
  };

  const leadPipelineData = {
    labels: ['New', 'Contacted', 'Interested', 'Site Visit', 'Quotation', 'Negotiation', 'Converted', 'Lost'],
    datasets: [{
      label: 'Leads',
      data: [6, 8, 10, 5, 7, 4, 6, 2],
      backgroundColor: ['#3B82F6','#8B5CF6','#F59E0B','#E8530A','#6366F1','#EC4899','#10B981','#EF4444'],
      borderRadius: 8,
    }],
  };

  const productMixData = {
    labels: ['Modular Kitchen', 'Wardrobe', 'Bedroom Set', 'Living Room', 'Full Home'],
    datasets: [{
      data: [35, 22, 18, 12, 13],
      backgroundColor: ['#E8530A','#FF6B2B','#FFAD89','#FFD4BF','#FFF0E8'],
      borderWidth: 0,
    }],
  };

  const barOpts = {
    responsive: true,
    plugins: { legend: { position: 'top', labels: { boxWidth: 10, font: { size: 11 } } }, tooltip: { callbacks: { label: (ctx) => ` ₹${(ctx.raw / 100000).toFixed(1)}L` } } },
    scales: { y: { grid: { color: 'rgba(0,0,0,0.04)' }, ticks: { callback: (v) => `₹${v / 100000}L`, font: { size: 10 } }, border: { display: false } }, x: { grid: { display: false }, ticks: { font: { size: 10 } }, border: { display: false } } },
  };

  const piOpts = { responsive: true, plugins: { legend: { position: 'right', labels: { boxWidth: 10, font: { size: 11 } } } }, cutout: '60%' };

  const salesTeam = [
    { name: 'Priya Sharma', leads: 24, converted: 8, revenue: 1200000 },
    { name: 'Arun Singh', leads: 15, converted: 5, revenue: 850000 },
    { name: 'Meera Patel', leads: 9, converted: 3, revenue: 520000 },
  ];

  return (
    <div className="space-y-5">
      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard title="Total Revenue (YTD)" value={formatCurrency(a.totalRevenue)} icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>} color="orange" trend={{ value: 24.5, label: 'YoY' }} />
        <StatCard title="Net Profit" value={formatCurrency(a.netProfit)} icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/></svg>} color="green" trend={{ value: 18.2, label: 'YoY' }} />
        <StatCard title="Profit Margin" value={`${Math.round((a.netProfit / (a.totalRevenue || 1)) * 100)}%`} icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10"/></svg>} color="purple" />
        <StatCard title="Avg Project Value" value={formatCurrency(a.totalRevenue / ((a.completedProjects + a.activeProjects) || 1))} icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z"/></svg>} color="blue" />
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="card p-5">
          <div className="mb-4">
            <h3 className="section-title">Revenue vs Material Cost</h3>
            <p className="text-xs text-blum-slate mt-0.5">Monthly comparison 2024</p>
          </div>
          <div style={{ height: 250 }}>
            <Bar data={revenueVsCost} options={barOpts} />
          </div>
        </div>

        <div className="card p-5">
          <div className="mb-4">
            <h3 className="section-title">Lead Pipeline Distribution</h3>
            <p className="text-xs text-blum-slate mt-0.5">Leads by stage</p>
          </div>
          <div style={{ height: 250 }}>
            <Bar data={leadPipelineData} options={{ ...barOpts, plugins: { ...barOpts.plugins, legend: { display: false } } }} />
          </div>
        </div>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="card p-5">
          <div className="mb-4">
            <h3 className="section-title">Product Mix</h3>
            <p className="text-xs text-blum-slate">Revenue by product</p>
          </div>
          <div style={{ height: 200 }}>
            <Doughnut data={productMixData} options={piOpts} />
          </div>
        </div>

        {/* Sales Team Performance */}
        <div className="card p-5 lg:col-span-2">
          <div className="mb-4">
            <h3 className="section-title">Sales Team Performance</h3>
            <p className="text-xs text-blum-slate">Current month stats</p>
          </div>
          <div className="space-y-4">
            {salesTeam.map((member, i) => (
              <div key={i}>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2.5">
                    <div className="w-7 h-7 bg-gradient-to-br from-orange-400 to-blum-orange rounded-full flex items-center justify-center">
                      <span className="text-white text-xs font-bold">{member.name.charAt(0)}</span>
                    </div>
                    <div>
                      <span className="text-sm font-semibold text-blum-dark">{member.name}</span>
                      <div className="flex gap-2 mt-0.5">
                        <span className="text-xs text-blum-slate">{member.leads} leads</span>
                        <span className="text-xs text-green-600 font-medium">{member.converted} converted</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold text-blum-orange">{formatCurrency(member.revenue)}</div>
                    <div className="text-xs text-blum-slate">{Math.round((member.converted / (member.leads || 1)) * 100)}% conversion</div>
                  </div>
                </div>
                <ProgressBar value={Math.round((member.revenue / 1500000) * 100)} color={i === 0 ? 'bg-blum-orange' : i === 1 ? 'bg-blue-400' : 'bg-purple-400'} />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Project Status Summary */}
      <div className="card p-5">
        <h3 className="section-title mb-4">Financial Summary</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Total Revenue', value: a.totalRevenue, color: 'text-blum-dark', bg: 'bg-gray-50' },
            { label: 'Material Costs', value: a.materialCosts, color: 'text-blue-600', bg: 'bg-blue-50' },
            { label: 'Labor & Overhead', value: Math.round(a.totalRevenue * 0.15), color: 'text-purple-600', bg: 'bg-purple-50' },
            { label: 'Net Profit', value: a.netProfit, color: 'text-green-600', bg: 'bg-green-50' },
          ].map(({ label, value, color, bg }) => (
            <div key={label} className={`${bg} rounded-xl p-4 text-center`}>
              <div className={`font-display font-bold text-xl ${color}`}>{formatCurrency(value)}</div>
              <div className="text-xs text-blum-slate mt-1">{label}</div>
              <div className="text-xs font-medium text-blum-dark mt-1">{Math.round((value / (a.totalRevenue || 1)) * 100)}% of revenue</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
